import { useMemo } from 'react';

import { BlockEditorGrid } from '@/components/blocks/editor/block-editor-grid';
import { SectionGridRenderer } from '@/components/blocks/renderers/section-grid-renderer';
import { useIsPageEditor } from '@/hooks/use-is-page-editor';
import type { CollectionItem } from '@/lib/blocks/collection/blocks';
import {
  hydrateIndexCollectionBlocks,
  type IndexCollectionContentType,
} from '@/lib/blocks/collection/index-collection/blocks';
import type { BlockStackLayoutConfig } from '@/lib/blocks/layout/block-stack-layout';
import type { PageBlock } from '@/types/block';

const INDEX_COLLECTION_STACK_LAYOUT = {
  direction: 'vertical',
  align: 'start',
  padding: 'none',
} satisfies BlockStackLayoutConfig;

export function IndexCollectionBlocks<T>({
  blocks,
  records,
  sectionId,
  editorSectionId,
  isStaticPreview,
  isPreview,
  variant,
  contentType,
  variants,
  toCollectionItem,
  extraVariantProps,
}: {
  blocks: PageBlock[];
  records: T[];
  sectionId?: string;
  editorSectionId?: string;
  isStaticPreview: boolean;
  isPreview: boolean;
  variant: string;
  contentType: IndexCollectionContentType;
  variants: { list: string; grid: string; split: string };
  toCollectionItem: (record: T, index: number, isPreview: boolean) => CollectionItem;
  extraVariantProps?: (variant: string) => Record<string, unknown>;
}) {
  const isPageEditor = useIsPageEditor();
  const hydratedBlocks = useMemo(
    () =>
      hydrateIndexCollectionBlocks({
        blocks,
        records,
        isPreview,
        variant,
        contentType,
        variants,
        toCollectionItem,
        extraVariantProps,
      }),
    [
      blocks,
      records,
      isPreview,
      variant,
      contentType,
      variants,
      toCollectionItem,
      extraVariantProps,
    ],
  );

  if (hydratedBlocks.length === 0) return null;

  if (isPageEditor && !isStaticPreview && sectionId) {
    return (
      <BlockEditorGrid
        sectionId={sectionId}
        editorSectionId={editorSectionId}
        blocks={hydratedBlocks}
        stackLayout={INDEX_COLLECTION_STACK_LAYOUT}
      />
    );
  }

  return (
    <SectionGridRenderer blocks={hydratedBlocks} stackLayout={INDEX_COLLECTION_STACK_LAYOUT} />
  );
}
